import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { FC, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as z from "zod";

import actions from "@/actions";
import { SubmitButton } from "@/components/common";
import { RootState } from "@/reducers/types";
import { Button } from "@/sx-layout/common/Button";
import { FormError, FormLabel, FormRow, InputText, Select, TextArea } from "@/sx-layout/common/Form";
import { MarkIcon } from "@/sx-layout/common/MarkIcon";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/sx-layout/common/Modal";
import { ColorPickerForm } from "@/sx-layout/components/plotmap/components/freeDraw/components/ColorPickerForm";
import { UserRole } from "@/sx-layout/components/plotmap/models";
import { formatUseHour, formatUseHourString } from "@/sx-layout/components/plotmap/util/formatUseHour";
import { Mark, MarkType, User } from "@/sx-layout/models";
import { HHMMRegex, normalizeHexColor } from "@/sx-layout/utils";

export type MarkEditorProps = {
  readonly markData?: Mark;
  readonly duplicate?: boolean; // 当日複製の場合はmarkDataをコピーして重機登録する
  readonly plotPlanId: string;
  readonly onClose: () => void;
  readonly initialX: number;
  readonly initialY: number;
};

export const MarkEditor: FC<MarkEditorProps> = ({ markData, duplicate, plotPlanId, onClose, initialX, initialY }) => {
  const { t } = useTranslation();
  const userRole = useSelector((state: RootState) => state.layoutApp.layoutMasters.user_role);
  const isAdmin = userRole === UserRole.MASTER_ROLE;

  const formSchema = z
    .object({
      company_id: z.union([z.number(), z.null()]).refine((value) => !isAdmin || value !== null, {
        message: t("select_company"),
      }),
      mark_type_id: z.union([z.number(), z.null()]).refine((value) => value !== null, {
        message: t("mark_is_required"),
      }),
      size: z.number(),
      color: z.string(),
      use_start_hour: z
        .string()
        .refine((hour) => hour !== "", { message: `${t("operation_time")}：${t("input_start_hour_validation")}` })
        .refine((hour) => HHMMRegex.test(hour) && hour !== "24:00", {
          message: `${t("operation_time")}：${t("error_start_hour")}`,
        }),
      use_end_hour: z
        .string()
        .refine((hour) => hour !== "", { message: `${t("operation_time")}：${t("input_end_hour_validation")}` })
        .refine((hour) => HHMMRegex.test(hour) && hour !== "24:00", {
          message: `${t("operation_time")}：${t("error_end_hour")}`,
        }),
      work_content: z
        .string()
        .min(1, { message: t("input_content_validation") })
        .max(100, { message: t("content_is_too_long_100") }),
      user_id: z.union([z.number(), z.null()]).refine((value) => value !== null, {
        message: t("assignee_is_required"),
      }),
      note: z.union([z.string().max(100, { message: t("note_is_too_long_100") }), z.null()]),
    })
    .refine((data) => formatUseHour(data.use_start_hour) < formatUseHour(data.use_end_hour), {
      message: t("work_time_alert"),
      path: ["use_end_hour"],
    });

  const mode: "create" | "update" | "duplicate" = duplicate ? "duplicate" : !markData ? "create" : "update";

  const dispatch = useDispatch();

  const layoutDate = useSelector<RootState, Date>((state) => state.plotmap.layoutDate);
  const layoutMasterUsers = useSelector((state: RootState) => state.layoutApp.layoutMasters.users);
  const companies = useSelector((state: RootState) => state.layoutApp.layoutMasters.companies);

  const userId = useSelector((state: RootState) => state.session.userId);
  const user = layoutMasterUsers.find((user) => user.user_id === userId);
  const company = companies?.find((c) => c.company_id === user.company_id);
  const initCompanyId = Number(markData?.company_id ?? company.company_id);

  const [phoneNumber, setPhoneNumber] = useState(mode === "create" ? "-----" : markData?.phone_number);
  const [companyName, setCompanyName] = useState(mode === "create" ? "-----" : markData?.company_name);

  const getUsers = (companyId: number): User[] => {
    return layoutMasterUsers?.filter((u) => u.company_id === companyId);
  };

  const [users, setUsers] = useState(() => getUsers(initCompanyId));

  type FormData = z.infer<typeof formSchema>;
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    reValidateMode: "onSubmit",
    defaultValues: {
      company_id: initCompanyId,
      mark_type_id: markData?.mark_type_id ?? null,
      size: markData?.size || 1,
      color: normalizeHexColor(markData?.color || "#ffffff"),
      use_start_hour: markData?.use_start_hour ?? "",
      use_end_hour: markData?.use_end_hour ?? "",
      work_content: markData?.work_content ?? "",
      user_id: markData?.user_id ?? null,
      note: markData?.note ?? "",
    },
  });

  const markTypeId = watch("mark_type_id");
  const color = watch("color");

  // 時間文字列のフォーマット
  const useStartHour = watch("use_start_hour");
  const onBlurUseStartHour = useCallback(() => {
    setValue("use_start_hour", formatUseHourString(useStartHour));
  }, [useStartHour]);
  const useEndHour = watch("use_end_hour");
  const onBlurUseEndHour = useCallback(() => {
    setValue("use_end_hour", formatUseHourString(useEndHour));
  }, [useEndHour]);

  // 会社の選択肢に連動して重機・使用者を表示する
  const onChangeCompany = (companyId: number) => {
    setUsers(getUsers(companyId));
    setValue("user_id", null);
    setCompanyName("-----");
    setPhoneNumber("-----");
  };
  // 重機を選択してアイコンを変えたら着色を修正する
  const onChangeMarkType = (value) => {
    setValue("color", Object.values(MarkType).find((m) => m.id === value)?.color ?? "#ffffff");
    setValue("mark_type_id", value);
  };

  // 使用者の選択肢に連動して所属・連絡先を表示する
  const onChangeUser = (userId: string) => {
    const user = layoutMasterUsers.find((user) => String(user.user_id) === userId);
    setPhoneNumber(user?.phone_number ?? "-----");
    const company = companies.find((c) => c.company_id === user?.company_id);
    setCompanyName(company?.company_name ?? "-----");
  };

  const [loading, setLoading] = useState(false);
  const onSubmit = (values: FormData) => {
    const user = layoutMasterUsers.find((user) => String(user.user_id) === String(values.user_id));
    const company = companies.find((c) => c.company_id === user.company_id);

    setLoading(true);
    if (mode === "create") {
      /* 新規作成 */
      dispatch(
        actions.mark.createMark(
          {
            company_id: company.company_id,
            plot_plan_id: Number(plotPlanId),
            layout_date: moment(layoutDate).format("YYYY-MM-DD").toString(),
            x: initialX,
            y: initialY,
            mark_type_id: values.mark_type_id!,
            use_start_hour: values.use_start_hour!,
            use_end_hour: values.use_end_hour!,
            work_content: values.work_content!,
            user_id: values.user_id!,
            color: values.color,
            size: values.size,
            note: values.note,
          },
          () => {
            setLoading(false);
            onClose();
          }
          // () => setLoading(false)
        )
      );
    } else {
      if (duplicate) {
        /* 当日複製 */
        dispatch(
          actions.mark.createMark(
            {
              company_id: company.company_id,
              plot_plan_id: Number(plotPlanId),
              layout_date: moment(layoutDate).format("YYYY-MM-DD").toString(),
              x: initialX,
              y: initialY,
              mark_type_id: values.mark_type_id!,
              use_start_hour: values.use_start_hour!,
              use_end_hour: values.use_end_hour!,
              work_content: values.work_content!,
              user_id: values.user_id!,
              color: values.color,
              size: values.size,
              note: values.note,
            },
            () => {
              setLoading(false);
              onClose();
            },
            () => setLoading(false)
          )
        );

        return;
      }

      /* 更新 */
      dispatch(
        actions.mark.updateMark(
          {
            mark_id: markData.mark_id,
            timestamp: {
              update_date: markData.timestamp.update_date,
            },
            company_id: company.company_id,
            plot_plan_id: Number(plotPlanId),
            layout_date: moment(layoutDate).format("YYYY-MM-DD").toString(),
            mark_type_id: values.mark_type_id!,
            use_start_hour: values.use_start_hour!,
            use_end_hour: values.use_end_hour!,
            work_content: values.work_content!,
            user_id: values.user_id!,
            color: values.color,
            size: values.size,
            note: values.note,
          },
          () => {
            setLoading(false);
            dispatch(actions.plotmap.fetchMarks(Number(plotPlanId), layoutDate));
            onClose();
          },
          () => setLoading(false)
        )
      );
    }
  };

  return (
    <Modal onClose={onClose} id="mark-editor-modal" shouldCloseOnEsc={false} shouldCloseOnOverlayClick={false}>
      <ModalHeader>{mode === "duplicate" ? t("duplicate_mark") : t("edit_mark")}</ModalHeader>
      <ModalBody className="modal-body items-start" style={{ maxHeight: "calc(100vh - 50px)" }}>
        <div className="mr-[60px]">
          <form>
            {/* 会社 */}
            {isAdmin && (
              <FormRow errorComponent={errors.company_id && <FormError>{errors.company_id.message}</FormError>}>
                <FormLabel>{t("company_title")}</FormLabel>
                {mode === "create" ? (
                  <Select
                    className="w-200"
                    // @ts-ignore
                    control={control}
                    name="company_id"
                    handleChange={onChangeCompany}
                    options={companies.map((c) => ({
                      label: c.company_name,
                      value: c.company_id,
                    }))}
                  />
                ) : (
                  <span className="w-[160px] px-[8px] ">{markData.company_name}</span>
                )}
              </FormRow>
            )}
            {/* マーク */}
            <FormRow errorComponent={errors.mark_type_id && <FormError>{errors.mark_type_id.message}</FormError>}>
              <FormLabel>{t("mark")}</FormLabel>
              <Select
                className="w-200"
                // @ts-ignore
                control={control}
                name="mark_type_id"
                isClearable={true}
                handleChange={onChangeMarkType}
                options={Object.values(MarkType).map((m) => ({
                  label: t(m.i18n),
                  value: m.id,
                }))}
              />
            </FormRow>
            {/* アイコン・色 */}
            <div className="flex">
              <FormRow errorComponent={errors.color ? <FormError>{errors.color.message}</FormError> : undefined}>
                <FormLabel>{t("icon")}</FormLabel>
                <div className="flex items-center gap-[10px]">
                  {/* アイコン */}
                  <div className={"w-[40px] h-[40px]"}>
                    <MarkIcon markTypeId={markTypeId} color={color} active={true} selected={false} outline={true} />
                  </div>
                  {/* 着色 */}
                  <span>{t("coloring")}</span>
                  {/* 色選択 */}
                  <ColorPickerForm control={control} name="color" defaultColor="#ffffff" hideInput={true} />
                </div>
              </FormRow>
            </div>
            {/* サイズ */}
            <FormRow errorComponent={errors.size && <FormError>{errors.size.message}</FormError>}>
              <FormLabel>{t("size")}</FormLabel>
              <Select
                className="w-100"
                // @ts-ignore
                control={control}
                name="size"
                isClearable={false}
                options={[
                  { value: 0.5, label: "50%" },
                  { value: 0.6, label: "60%" },
                  { value: 0.7, label: "70%" },
                  { value: 0.8, label: "80%" },
                  { value: 0.9, label: "90%" },
                  { value: 1, label: "100%" },
                  { value: 1.1, label: "110%" },
                  { value: 1.2, label: "120%" },
                  { value: 1.3, label: "130%" },
                  { value: 1.4, label: "140%" },
                  { value: 1.5, label: "150%" },
                  { value: 1.6, label: "160%" },
                  { value: 1.7, label: "170%" },
                  { value: 1.8, label: "180%" },
                  { value: 1.9, label: "190%" },
                  { value: 2.0, label: "200%" },
                ]}
              />
            </FormRow>
            <FormRow
              errorComponent={
                <>
                  {errors.use_start_hour && <FormError>{errors.use_start_hour.message}</FormError>}
                  {errors.use_end_hour && <FormError>{errors.use_end_hour.message}</FormError>}
                </>
              }
            >
              <FormLabel>{t("operation_time")}</FormLabel>
              <InputText className="w-[80px]" {...register("use_start_hour")} onBlur={onBlurUseStartHour} />
              <span className="mx-3">〜</span>
              <InputText
                className="w-[80px]"
                {...register("use_end_hour", { deps: ["use_start_hour"] })}
                onBlur={onBlurUseEndHour}
              />
            </FormRow>
            <FormRow errorComponent={errors.work_content && <FormError>{errors.work_content.message}</FormError>}>
              <FormLabel>{t("content")}</FormLabel>
              <InputText
                className="w-[300px] px-[8px] py-[4px] border border-[#000] rounded"
                {...register("work_content")}
              />
            </FormRow>

            <FormRow>
              <FormLabel>{t("belonging")}</FormLabel>
              <span className="w-[300px] px-[8px] py-[4px]">{companyName}</span>
            </FormRow>
            {/* 担当者 */}
            <FormRow errorComponent={errors.user_id && <FormError>{errors.user_id.message}</FormError>}>
              <FormLabel>{t("assignee")}</FormLabel>
              <Select
                className="w-200"
                // @ts-ignore
                control={control}
                name="user_id"
                handleChange={(value) => onChangeUser(String(value))}
                isClearable={true}
                options={users.map((user) => ({
                  label: user.user_name,
                  value: user.user_id,
                }))}
              />
            </FormRow>

            <FormRow>
              <FormLabel>{t("contact")}</FormLabel>
              <span className="w-[160px] px-[8px] py-[4px]">{phoneNumber}</span>
            </FormRow>

            <FormRow
              className="items-start"
              errorComponent={errors.note && <FormError>{errors.note.message}</FormError>}
            >
              <FormLabel className="mt-[4px]">{t("note")}</FormLabel>
              <TextArea
                className="w-[300px] border border-[#000] rounded px-[8px] py-[4px]"
                rows={5}
                {...register("note")}
              />
            </FormRow>
          </form>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button buttonType="cancel" onClick={onClose}>
          {t("cancel")}
        </Button>
        <SubmitButton onClick={handleSubmit(onSubmit)} loading={loading} layout={true} />
      </ModalFooter>
    </Modal>
  );
};
